import { faCheck, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ContractedIcon from 'src/assets/icons/ContractedIcon';
import DownloadIcon from 'src/assets/icons/DownloadIcon';
import Button from 'src/components/design-system/Button';
import useConfirm from 'src/hooks/useConfirm';
import { handlePostMessage } from 'src/hooks/usePostMessage';
import useWindowSize from 'src/hooks/useWindowSize';
import SaveRecommendationItems from 'src/sections/recommended_items/SaveRecommendationItems';
import useDeleteCartMutation from 'src/services/cart/useDeleteCartMutation';
import useGetCartNotRecommendedItemsQuery from 'src/services/cart/useGetCartNotRecommendedItemsQuery';
import useUpdateCartMutation from 'src/services/cart/useUpdateCartMutation';
import useGetProductDetailsQuery from 'src/services/catalog_products/useGetProductDetailsQuery';
import { useGetPoNotRecommendedLineItemsQuery } from 'src/services/po';
import useDeletePoLineItemMutation from 'src/services/po/useDeletePoLineItemMutation';
import useUpdatePoLineItemMutation from 'src/services/po/useUpdatePoLineItemMutation';
import useRecommendationItemMutation from 'src/services/recommendation_items/useRecommendationItemMutation';
import {
  RecommendationItem,
  RecommendationItemResponse,
  RecommendPageParams,
} from 'src/types/recommendation_items';
import {
  buildModifiedPanelExportRows,
  exportRecommendationsExcel,
  getRecommendationExportDateString,
  MODIFIED_PANEL_COLUMN_MAPPING,
  MODIFIED_PANEL_COLUMNS,
  RECOMMENDATION_DISCLAIMER,
  RECOMMENDATION_EXPORT_COLUMN_MAPPING,
  RECOMMENDATION_EXPORT_COLUMNS,
} from 'src/utils/recommendationExcelExport';

export interface RecommendedItemsResponse {
  supplierId: number;
  itemNumber: string;
  recommendations: RecommendationItemResponse[];
}

const PAGE_TYPE = {
  PO: 0,
  CART: 1,
  FAVOURITE: 2,
  SEARCH: 3,
  CATALOG: 4,
};

/** Decode the base64 `data` query string value back into RecommendParams (UTF-8 safe) */
const decodeRecommendParams = (encoded: string): RecommendPageParams | null => {
  try {
    const binary = window.atob(encoded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const jsonStr = new TextDecoder().decode(bytes);
    return JSON.parse(jsonStr) as RecommendPageParams;
  } catch {
    return null;
  }
};

const CATALOG_TYPE = {
  ONLINE: 1,
  PUNCHOUT: 2,
  OFFLINE: 3,
};

function getAllowedOperationsLabel(
  item: RecommendationItem,
  blockContext: RecommendPageParams | null | undefined,
  pageType: number
): string {
  if (!item.isAvailableInSystem) return 'NA';
  const isPunchout = item.catalogType === CATALOG_TYPE.PUNCHOUT;
  const isSameSupplier = item.supplierId === blockContext?.supplierId;
  const isEditable = blockContext?.canDelete ?? false;
  const isPunchoutSwap = isPunchout && isSameSupplier && isEditable;
  if (isPunchoutSwap) return 'Swap Item';
  if (isPunchout) return 'Add to Punchout List';
  if (pageType === PAGE_TYPE.PO)
    return item.supplierId !== blockContext?.supplierId ? 'Add to Cart' : 'Swap Item';
  return pageType === PAGE_TYPE.CART ? 'Swap Item' : 'Add to Cart';
}

export type StagedActionType = 'swap' | 'addToCart' | 'punchout';

export interface StagedAction {
  action: StagedActionType;
  item: RecommendationItem;
}

type BlockDisplayInfo = {
  description: string;
  price: string;
  uom: string;
};

/** Same layout as former `lg:` utilities, but driven by this value (viewport width) instead of Tailwind breakpoints. */
const RECOMMENDATION_ITEM_DETAIL_ACTION_ROW_MIN_WIDTH_PX = 600;

interface RecommendationItemsProps {
  isInternal: boolean;
  dataParams?: RecommendPageParams | null;
  onClose: (pageRefresh: boolean) => void;
}

const RecommendationItems = ({ isInternal, dataParams, onClose }: RecommendationItemsProps) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const {
    mutateAsync: getRecommendedItems,
    data: recommendedItems,
    isPending,
    isError,
    reset: resetRecommendedItems,
  } = useRecommendationItemMutation();

  // Decode the serialized RecommendParams from the `data` query param
  const [searchParams] = useSearchParams();
  const pageParams = useMemo(() => {
    if (isInternal && dataParams) {
      return dataParams;
    }
    const encoded = searchParams.get('data');
    if (!encoded) return null;
    return decodeRecommendParams(encoded);
  }, [searchParams, dataParams, isInternal]);

  const page = pageParams?.pageType as number;

  const isSingleItemPage =
    page === PAGE_TYPE.FAVOURITE || page === PAGE_TYPE.SEARCH || page === PAGE_TYPE.CATALOG;
  const isBulkPoPage = page === PAGE_TYPE.PO;
  const isBulkCartPage = page === PAGE_TYPE.CART;

  const {
    data: productDetails,
    isFetching: isProductDetailsLoading,
    isError: isProductDetailsError,
  } = useGetProductDetailsQuery(
    pageParams?.productId ?? null,
    pageParams?.catalogId ?? null,
    pageParams?.categoryId ?? null,
    // Fetch for single-item pages (FAVOURITE/SEARCH/CATALOG) or when CART with isBulkRequest false
    isSingleItemPage || (isBulkCartPage && !pageParams?.isBulkRequest)
  );

  // Cart related apis
  const {
    data: cartNotRecommendedItems,
    isFetching: isCartListLoading,
    isError: isCartNotRecommendedError,
  } = useGetCartNotRecommendedItemsQuery({
    enabled: isBulkCartPage && Boolean(pageParams?.isBulkRequest),
  });
  const { mutateAsync: updateCart } = useUpdateCartMutation();
  const { mutateAsync: deleteCartItem } = useDeleteCartMutation();

  // PO related apis
  const {
    data: poNotRecommendedLineItems,
    isFetching: isPoListLoading,
    isError: isPoNotRecommendedError,
  } = useGetPoNotRecommendedLineItemsQuery({
    poId: pageParams?.poId,
    lineItemId: pageParams?.isBulkRequest ? null : pageParams?.lineItemId,
    enabled: isBulkPoPage,
  });
  const { mutateAsync: updatePoLineItem } = useUpdatePoLineItemMutation();
  const { mutateAsync: deletePoLineItem } = useDeletePoLineItemMutation();

  // Single-item pages or non-bulk Cart: fetch recommendations directly from pageParams
  const isSingleItemOrNonBulkCart =
    isSingleItemPage || (isBulkCartPage && !pageParams?.isBulkRequest);

  useEffect(() => {
    if (!isSingleItemOrNonBulkCart) return;
    if (pageParams?.supplierCompanyId && pageParams?.itemNumber) {
      getRecommendedItems({
        items: [
          {
            supplierCompanyId: pageParams.supplierCompanyId,
            itemNumber: pageParams.itemNumber,
            price: pageParams.price,
            uom: pageParams.uom,
          },
        ],
      });
    } else {
      resetRecommendedItems();
    }
  }, [
    isSingleItemOrNonBulkCart,
    pageParams?.supplierCompanyId,
    pageParams?.itemNumber,
    getRecommendedItems,
    resetRecommendedItems,
    pageParams?.price,
    pageParams?.uom,
  ]);

  // PO page (bulk and non-bulk): fetch recommendations from the not-recommended line items API response.
  // Bulk → lineItemId omitted (all items); non-bulk → lineItemId passed (single item).
  useEffect(() => {
    if (!isBulkPoPage) return;
    if (!poNotRecommendedLineItems?.length) {
      resetRecommendedItems();
      return;
    }
    getRecommendedItems({
      items: poNotRecommendedLineItems.map((po) => ({
        supplierCompanyId: po.supplierCompanyId,
        itemNumber: po.itemNum,
        price: po.price,
        uom: po.uom,
      })),
      buyerClientId: poNotRecommendedLineItems[0].buyerClientId,
      buyerCompanyId: poNotRecommendedLineItems[0].buyerCompanyId,
    });
  }, [isBulkPoPage, poNotRecommendedLineItems, getRecommendedItems, resetRecommendedItems]);

  // Cart bulk: fetch recommendations when we have the list of not-recommended cart items (only when isBulkRequest)
  useEffect(() => {
    if (!isBulkCartPage || !pageParams?.isBulkRequest) return;
    if (!cartNotRecommendedItems?.length) {
      resetRecommendedItems();
      return;
    }
    getRecommendedItems({
      items: cartNotRecommendedItems.map((c) => ({
        supplierCompanyId: c.supplierCompanyId,
        itemNumber: c.itemNum,
        price: Number(c.price),
        uom: c.uom,
      })),
    });
  }, [
    isBulkCartPage,
    pageParams?.isBulkRequest,
    cartNotRecommendedItems,
    getRecommendedItems,
    resetRecommendedItems,
  ]);

  // Per-block context and display info (for PO/CART bulk we have one entry per source item)
  const blockContextList = useMemo((): (RecommendPageParams | null)[] => {
    // PO (bulk and non-bulk): build context from the API response so fields like
    // lineItemId, poGridId, glCode etc. always reflect server data.
    if (isBulkPoPage && poNotRecommendedLineItems?.length && pageParams) {
      return poNotRecommendedLineItems.map((po) => ({
        ...pageParams,
        lineItemId: po.lineItemId,
        supplierId: po.supplierId,
        supplierCompanyId: po.supplierCompanyId,
        itemNumber: po.itemNum,
        quantity: po.quantity,
        glCode: po.glCode,
        productId: po.productId,
        catalogId: po.catalogId,
        categoryId: po.categoryId,
        poGridId: po.poGridId,
        price: po.price,
        uom: po.uom,
      }));
    }
    if (
      isBulkCartPage &&
      pageParams?.isBulkRequest &&
      cartNotRecommendedItems?.length &&
      pageParams
    ) {
      return cartNotRecommendedItems.map((c) => ({
        ...pageParams,
        orderBinId: c.clientOrderBinId,
        supplierCompanyId: c.supplierCompanyId,
        itemNumber: c.itemNum,
        quantity: c.quantityOrdered,
        price: Number(c.price),
        uom: c.uom,
      }));
    }
    // Single-item page or non-bulk Cart: one block from pageParams
    if ((isSingleItemPage || isBulkCartPage) && pageParams) return [pageParams];
    return [];
  }, [
    isBulkPoPage,
    isBulkCartPage,
    isSingleItemPage,
    pageParams,
    poNotRecommendedLineItems,
    cartNotRecommendedItems,
  ]);

  const blockDisplayList = useMemo((): BlockDisplayInfo[] => {
    // PO (bulk and non-bulk): use the API response data.
    if (isBulkPoPage && poNotRecommendedLineItems?.length) {
      return poNotRecommendedLineItems.map((po) => ({
        description: po.description + ' #' + po.itemNum,
        price: po.price != null ? String(po.price) : '',
        uom: po.uom ?? '',
      }));
    }
    if (isBulkCartPage && pageParams?.isBulkRequest && cartNotRecommendedItems?.length) {
      return cartNotRecommendedItems.map((c) => ({
        description: c.description + ' #' + c.itemNum,
        price: c.price ?? '',
        uom: c.uom ?? '',
      }));
    }
    // Single-item page or non-bulk Cart: use product details
    if ((isSingleItemPage || isBulkCartPage) && productDetails) {
      return [
        {
          description: productDetails.description + ' #' + productDetails.itemNum,
          price: productDetails.price ?? '',
          uom: productDetails.uom ?? '',
        },
      ];
    }
    return [];
  }, [
    isBulkPoPage,
    isBulkCartPage,
    isSingleItemPage,
    pageParams?.isBulkRequest,
    poNotRecommendedLineItems,
    cartNotRecommendedItems,
    productDetails,
  ]);

  const isListLoading = isBulkPoPage
    ? isPoListLoading
    : isBulkCartPage && pageParams?.isBulkRequest
    ? isCartListLoading
    : isProductDetailsLoading;

  /** Execute PO Add to Cart (no close/toast); used by bulk save */
  const executePOAddToCart = useCallback(
    async (item: RecommendationItem, blockContext?: RecommendPageParams | null) => {
      if (!blockContext) return;
      if (!blockContext?.poId || !blockContext?.lineItemId) {
        throw new Error('Missing PO ID or LineItem ID.');
      }
      await updateCart({
        items: [
          {
            productId: item.productId,
            catalogId: item.catalogId,
            categoryId: item.categoryId,
            quantity: blockContext?.quantity || 1,
            isCompanySpecified: item.isCompanySpecified,
            glCode: blockContext?.glCode ?? '',
          },
        ],
      });
      if (blockContext.canDelete) {
        await deletePoLineItem({
          poId: blockContext.poId,
          lineItemId: blockContext.lineItemId,
          poGridId: blockContext.poGridId,
        });
      }
    },
    [updateCart, deletePoLineItem]
  );

  /** Execute PO Swap (no close/toast); used by bulk save */
  const executePoSwapItem = useCallback(
    async (item: RecommendationItem, blockContext?: RecommendPageParams | null) => {
      if (!blockContext) return;
      if (!blockContext?.supplierId || !blockContext?.poId || !blockContext?.lineItemId) {
        throw new Error('Missing Supplier ID or PO ID or LineItem ID.');
      }
      await updatePoLineItem({
        supplierId: blockContext.supplierId,
        poId: blockContext.poId,
        lineItemNumber: blockContext.lineItemId,
        lineItemApproval: 1,
        quantity: blockContext?.quantity || 1,
        itemNumber: item.itemNumber,
        previousItemNumber: blockContext?.itemNumber ?? '',
        itemDescription: item.description,
        glCode: blockContext?.glCode ?? '',
        price: item.price,
        weight: item.weight,
        uom: item.uom,
        productId: item.productId,
        catalogId: item.catalogId,
        categoryId: item.categoryId,
      });
    },
    [updatePoLineItem]
  );

  /** Execute Cart Swap (no close/toast); used by bulk save */
  const executeSwapItem = useCallback(
    async (item: RecommendationItem, blockContext?: RecommendPageParams | null) => {
      if (!blockContext) return;
      const originalOrderBinId = blockContext?.orderBinId;
      if (!originalOrderBinId) throw new Error('Original order bin id not found.');
      await updateCart({
        items: [
          {
            productId: item.productId,
            catalogId: item.catalogId,
            categoryId: item.categoryId,
            quantity: blockContext?.quantity || 1,
            isCompanySpecified: item.isCompanySpecified,
          },
        ],
      });
      await deleteCartItem([originalOrderBinId]);
    },
    [deleteCartItem, updateCart]
  );

  /** Execute Add to Cart (no close/toast); used by bulk save */
  const executeAddToCart = useCallback(
    async (item: RecommendationItem, blockContext?: RecommendPageParams | null) => {
      if (!blockContext) return;
      await updateCart({
        items: [
          {
            productId: item.productId,
            catalogId: item.catalogId,
            categoryId: item.categoryId,
            quantity: blockContext?.quantity || 1,
            isCompanySpecified: item.isCompanySpecified,
          },
        ],
      });
    },
    [updateCart]
  );

  // Removes PO line item or cart item depending on page, so user can punchout.
  const executePunchout = useCallback(
    async (blockContext: RecommendPageParams | null) => {
      if (!blockContext) return;

      if (blockContext.pageType === PAGE_TYPE.PO && blockContext.canDelete) {
        if (!blockContext.poId || !blockContext.lineItemId) {
          throw new Error('Missing PO ID or LineItem ID.');
        }
        await deletePoLineItem({
          poId: blockContext.poId,
          lineItemId: blockContext.lineItemId,
          poGridId: blockContext.poGridId,
        });
      } else if (blockContext.pageType === PAGE_TYPE.CART) {
        const { orderBinId } = blockContext;
        if (!orderBinId) throw new Error('Missing order bin ID.');
        await deleteCartItem([orderBinId]);
      }
    },
    [deletePoLineItem, deleteCartItem]
  );

  const { confirm } = useConfirm();

  const [stagedActions, setStagedActions] = useState<Record<number, StagedAction>>({});
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState<
    Record<number, 'pending' | 'saving' | 'success' | 'error'>
  >({});
  const [saveCompletedSuccessfully, setSaveCompletedSuccessfully] = useState(false);

  const hasStagedActions = useMemo(() => Object.keys(stagedActions).length > 0, [stagedActions]);
  const showSavingUI = Object.keys(saveProgress).length > 0;

  const stageAction = useCallback(
    (blockIndex: number, action: StagedActionType, item: RecommendationItem) => {
      setStagedActions((prev) => ({ ...prev, [blockIndex]: { action, item } }));
    },
    []
  );

  const undoStage = useCallback((blockIndex: number) => {
    setStagedActions((prev) => {
      const next = { ...prev };
      delete next[blockIndex];
      return next;
    });
  }, []);

  const handleBulkSave = useCallback(async () => {
    if (!hasStagedActions || isBulkSaving) return;
    const entries = (Object.entries(stagedActions) as [string, StagedAction][]).sort(
      ([a], [b]) => Number(a) - Number(b)
    );

    const initialProgress: Record<number, 'pending' | 'saving' | 'success' | 'error'> = {};
    entries.forEach(([k]) => {
      initialProgress[Number(k)] = 'saving';
    });
    setSaveProgress(initialProgress);
    setIsBulkSaving(true);

    const results = await Promise.allSettled(
      entries.map(async ([blockIndexStr, { action, item }]) => {
        const blockIndex = Number(blockIndexStr);
        const blockContext = blockContextList[blockIndex] ?? null;
        if (action === 'addToCart') {
          if (page === PAGE_TYPE.PO) await executePOAddToCart(item, blockContext);
          else await executeAddToCart(item, blockContext);
        } else if (action === 'swap') {
          if (page === PAGE_TYPE.PO) await executePoSwapItem(item, blockContext);
          else await executeSwapItem(item, blockContext);
        } else if (action === 'punchout') {
          await executePunchout(blockContext);
        }
        return blockIndex;
      })
    );

    const finalProgress: Record<number, 'pending' | 'saving' | 'success' | 'error'> = {};
    results.forEach((result, i) => {
      const blockIndex = Number(entries[i][0]);
      finalProgress[blockIndex] = result.status === 'fulfilled' ? 'success' : 'error';
    });
    setSaveProgress(finalProgress);
    setIsBulkSaving(false);

    const allSucceeded = results.every((r) => r.status === 'fulfilled');
    if (allSucceeded) {
      setSaveCompletedSuccessfully(true);
      handlePostMessage({ cartUpdated: true });
    }
  }, [
    hasStagedActions,
    isBulkSaving,
    stagedActions,
    blockContextList,
    page,
    executePOAddToCart,
    executeAddToCart,
    executePoSwapItem,
    executeSwapItem,
    executePunchout,
  ]);

  const handleCancelClick = useCallback(async () => {
    if (isBulkSaving) return;
    if (hasStagedActions) {
      const result = await confirm({
        title: t('Discard Unsaved Changes'),
        content: t('recommendationUnsavedChanges'),
        confirmButtonText: t('Save'),
        cancelButtonText: t('Discard'),
      });
      if (result) {
        await handleBulkSave();
      } else {
        setStagedActions({});
        onClose(false);
      }
    } else {
      onClose(false);
    }
  }, [isBulkSaving, hasStagedActions, confirm, t, handleBulkSave, onClose]);

  const saveDisabled = !hasStagedActions || isBulkSaving || saveCompletedSuccessfully;
  const cancelDisabled = isBulkSaving;

  const handleCloseClick = useCallback(() => {
    setStagedActions({});
    setSaveProgress({});
    setSaveCompletedSuccessfully(false);
    if (isBulkPoPage || isBulkCartPage) {
      onClose(true);
    } else {
      onClose(false);
    }
  }, [isBulkCartPage, isBulkPoPage, onClose]);

  const handleExportRecommendations = useCallback(() => {
    if (!recommendedItems?.length || !blockDisplayList.length || !blockContextList.length) return;
    const rows: Record<string, string | number>[] = [];
    const groupRowCounts: number[] = [];

    recommendedItems.forEach((response, i) => {
      const blockContext = blockContextList[i] ?? null;
      const blockDisplay = blockDisplayList[i];
      const itemNum = blockContext?.itemNumber ?? '';
      const fullDesc = blockDisplay?.description ?? '';
      const productName =
        itemNum && fullDesc.endsWith(' ' + itemNum)
          ? fullDesc.slice(0, -(itemNum.length + 1)).trim()
          : fullDesc;
      const originalQty = blockContext?.quantity ?? '';
      const poLineItemNum = blockContext?.lineItemId != null ? String(blockContext.lineItemId) : '';

      const recs = response.recommendations ?? [];
      const groupCount = recs.length || 1;

      if (recs.length) {
        recs.forEach((rec, idx) => {
          const isFirst = idx === 0;
          rows.push({
            poLineItemNum: isFirst ? poLineItemNum : '',
            productName: isFirst ? productName : '',
            itemNum: isFirst ? itemNum : '',
            originalQty: isFirst ? originalQty : '',
            recommendationRank: rec.rank,
            recommendedProductName: rec.description ?? '',
            recommendedItemNumber: rec.itemNumber ?? '',
            recommendedDistributor: rec.supplierName ?? '',
            manufacturer: rec.manufacturerName ?? '',
            mfgNumber: rec.manufacturerNumber ?? '',
            allowedOperations: getAllowedOperationsLabel(rec, blockContext, page),
            preferredManufacturer: '',
          });
        });
      } else {
        rows.push({
          poLineItemNum,
          productName,
          itemNum,
          originalQty,
          recommendationRank: '',
          recommendedProductName: '',
          recommendedItemNumber: '',
          recommendedDistributor: '',
          manufacturer: '',
          mfgNumber: '',
          allowedOperations: 'N/A',
          preferredManufacturer: response.prefManufacturer?.manufacturerName ?? '',
        });
      }
      groupRowCounts.push(groupCount);
    });

    const dateStr = getRecommendationExportDateString();
    exportRecommendationsExcel({
      columns: [...RECOMMENDATION_EXPORT_COLUMNS],
      rows,
      columnMapping: RECOMMENDATION_EXPORT_COLUMN_MAPPING,
      fileName: `PO_All_Contracted_Alternatives_${dateStr}`,
      sheetName: t('Recommendation Summary'),
      message: RECOMMENDATION_DISCLAIMER,
      blankRowAfterMessage: true,
      mergeLeadingColumnCount: 4,
      groupRowCounts,
    });
  }, [recommendedItems, blockDisplayList, blockContextList, page, t]);

  const handleExportModifiedList = useCallback(() => {
    const entries = (Object.entries(stagedActions) as [string, StagedAction][])
      .filter(([blockIndexStr]) => saveProgress[Number(blockIndexStr)] != null)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([blockIndexStr, staged]) => {
        const blockIndex = Number(blockIndexStr);
        const ctx = blockContextList[blockIndex] ?? null;
        return {
          staged,
          blockDisplay: blockDisplayList[blockIndex],
          blockContext: ctx,
        };
      });
    if (!entries.length) return;
    const rows = buildModifiedPanelExportRows(entries);
    const groupRowCounts = entries.map(() => 1);
    const prefix = page === PAGE_TYPE.PO ? 'PO' : 'Cart';
    const dateStr = getRecommendationExportDateString();
    exportRecommendationsExcel({
      columns: [...MODIFIED_PANEL_COLUMNS],
      rows,
      columnMapping: MODIFIED_PANEL_COLUMN_MAPPING,
      fileName: `${prefix}_Selected_Alternatives_Summary_${dateStr}`,
      sheetName: t('Modified Recommendations'),
      message: RECOMMENDATION_DISCLAIMER,
      blankRowAfterMessage: true,
      mergeLeadingColumnCount: 4,
      groupRowCounts,
    });
  }, [stagedActions, saveProgress, blockContextList, blockDisplayList, page, t]);

  const headerButtons = !showSavingUI ? (
    <div className="flex items-center gap-2">
      {isBulkPoPage && (
        <Button
          data-tooltip-id="recommended-panel-tooltip"
          data-tooltip-content={`${t('Download all Contracted Alternatives')}`}
          color="secondary"
          size="md"
          onClick={handleExportRecommendations}
          disabled={!recommendedItems?.length || isPending}
        >
          <DownloadIcon width="14" height="14" />
        </Button>
      )}
      <Button color="secondary" onClick={handleCancelClick} size="md" disabled={cancelDisabled}>
        {t('Cancel')}
      </Button>
      <Button color="secondary" disabled={saveDisabled} size="md" onClick={handleBulkSave}>
        {t('Save')}
      </Button>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      {(isBulkPoPage || isBulkCartPage) && (
        <Button
          data-tooltip-id="recommended-panel-tooltip"
          data-tooltip-content={`${t('Download Modified List')}`}
          color="secondary"
          size="md"
          onClick={handleExportModifiedList}
        >
          <DownloadIcon width="14" height="14" />
        </Button>
      )}
      <Button color="secondary" onClick={handleCloseClick} size="md">
        {t('Close')}
      </Button>
    </div>
  );

  const contractedAlternativesHeaderTitle = pageParams?.isBulkRequest
    ? t('Review all Contracted Alternative(s)')
    : t('Review Contracted Alternative(s)');

  const headerBar = (
    <div className="flex w-full min-w-0 flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 overflow-x-hidden bg-[#0E80B4] px-[32px] py-[10px]">
      <span
        className="max-w-full shrink-0 whitespace-nowrap pr-2 text-[18px] font-bold text-white"
        title={contractedAlternativesHeaderTitle}
      >
        {contractedAlternativesHeaderTitle}
      </span>
      <div className="flex shrink-0 flex-row flex-wrap items-center justify-end gap-2">
        {headerButtons}
      </div>
    </div>
  );

  const bodyContent = (
    <div className="flex w-full min-w-0 max-w-full flex-col space-y-2">
      {isListLoading || isPending ? (
        <>
          <div className="text-[#000000] text-[13px] mb-4">
            <p className="mb-2">{t('ourSystemWillAnalyzeThisItem')}</p>
          </div>
          <div className="flex items-center gap-3 border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
            <span className="k-icon k-i-loading k-icon-sm text-gray-400 animate-spin" />
            <span className="font-medium text-[#000000] text-sm">
              {t('ourSystemIsAnalyzingAlternatives')}
            </span>
          </div>
        </>
      ) : isError ||
        isProductDetailsError ||
        isCartNotRecommendedError ||
        isPoNotRecommendedError ||
        !recommendedItems?.length ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <img src="/assets/images/broken-page.png" alt="Error" className="w-20 h-20" />
          <div className="flex flex-col gap-1 mt-2 max-w-xs">
            <p className="text-[18px] font-bold text-gray-700">{t('somethingWentWrong')}</p>
            <p className="text-[13px] text-gray-500">{t('serverErrorDescription')}</p>
          </div>
        </div>
      ) : (
        <>
          {page === PAGE_TYPE.PO && !blockContextList[0]?.canDelete ? (
            <div className="bg-[#EE404C33] p-2">
              <p className="text-[13px]">{t('punchoutPartnerCannotAllowChangesToPurchaseOrder')}</p>
            </div>
          ) : (
            <p className="text-[#000000] text-[13px]">
              <span className="text-red-500">*</span>
              {t('itemMayBeSoldInADifferentCasePackOrUnitSize')}
            </p>
          )}
          {recommendedItems?.map((recommendedItem, _index) => {
            const blockContext = blockContextList[_index] ?? null;
            const blockDisplay = blockDisplayList[_index];
            const staged = stagedActions[_index];
            const recommendationsToShow =
              staged != null
                ? recommendedItem.recommendations.filter(
                    (rec) =>
                      rec.rank === staged.item.rank &&
                      rec.productId === staged.item.productId &&
                      rec.catalogId === staged.item.catalogId &&
                      rec.supplierId === staged.item.supplierId
                  )
                : recommendedItem.recommendations;
            return (
              <div
                key={`${recommendedItem.supplierId}-${recommendedItem.itemNumber}-${_index}`}
                className="border border-[#E5E7EB] rounded-lg p-4 bg-white shadow-sm space-y-2"
              >
                {/* Item information */}
                <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {blockContext?.lineItemId && (
                      <span className="flex-shrink-0 rounded-full bg-[#3FA9DB33] px-2.5 py-1 text-xs font-medium text-black">
                        Item {blockContext.lineItemId}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-[13px] text-[#000000]">
                        {blockDisplay?.description ?? ''}
                      </h3>
                      {blockDisplay?.price != null && blockDisplay?.price !== '' && (
                        <p className="mt-1 text-[13px] text-[#000000]">
                          <span className="font-semibold">
                            {`$${Number(blockDisplay.price).toFixed(2)}`}
                          </span>
                          {blockDisplay?.uom ? ` / ${blockDisplay?.uom}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  {staged != null && !isBulkSaving && (
                    <button
                      type="button"
                      onClick={() => undoStage(_index)}
                      className="flex-shrink-0 flex items-center gap-1 text-[13px] font-medium text-info cursor-pointer"
                    >
                      <FontAwesomeIcon icon={faRotateLeft} className="w-3.5 h-3.5" />
                      {t('Undo')}
                    </button>
                  )}
                </div>
                {recommendationsToShow.length > 0 ? (
                  <>
                    {recommendationsToShow.map((item) => {
                      const isUomMismatch =
                        item.uom?.trim().toLocaleLowerCase() !==
                        (blockDisplay?.uom || productDetails?.uom)?.trim().toLocaleLowerCase();
                      const isPunchout = item.catalogType === CATALOG_TYPE.PUNCHOUT;
                      const isSameSupplier = item.supplierId === blockContext?.supplierId;
                      const isEditable = blockContext?.canDelete ?? false;
                      const isThisStaged =
                        staged != null &&
                        staged.item.rank === item.rank &&
                        staged.item.productId === item.productId &&
                        staged.item.catalogId === item.catalogId &&
                        staged.item.supplierId === item.supplierId;
                      const isPunchoutSwap = isPunchout && isSameSupplier && isEditable;
                      const getActionLabel = () => {
                        if (isPunchoutSwap) return t('Swap Item');
                        if (isPunchout) {
                          return isThisStaged
                            ? t('Added to Punchout List')
                            : t('Add to Punchout List');
                        }
                        if (page === PAGE_TYPE.PO)
                          return item.supplierId !== blockContext?.supplierId
                            ? t('Add to Cart')
                            : t('Swap Item');
                        return page === PAGE_TYPE.CART ? t('Swap Item') : t('Add to Cart');
                      };
                      const getStagedActionType = (): StagedActionType => {
                        if (isPunchoutSwap) return 'swap';
                        if (isPunchout) return 'punchout';
                        if (page === PAGE_TYPE.PO)
                          return item.supplierId !== blockContext?.supplierId
                            ? 'addToCart'
                            : 'swap';
                        return page === PAGE_TYPE.CART ? 'swap' : 'addToCart';
                      };
                      const stackDetailActionRow =
                        width > 0 && width < RECOMMENDATION_ITEM_DETAIL_ACTION_ROW_MIN_WIDTH_PX;
                      return (
                        <div
                          key={`${item.productId}-${item.supplierId}-${item.catalogId}`}
                          className={`border border-[#D1D1D1] rounded-lg bg-white p-2 space-y-2 ${
                            blockContext?.lineItemId ? 'ml-6' : ''
                          }`}
                        >
                          <div>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-center min-w-0 overflow-hidden font-semibold text-[13px] text-gray-900 gap-2">
                                <span
                                  className="min-w-0 flex-1 truncate"
                                  {...(item.description
                                    ? {
                                        'data-tooltip-id': 'recommended-panel-tooltip',
                                        'data-tooltip-content': item.description,
                                      }
                                    : {})}
                                >
                                  {item.description}
                                </span>
                                {item.isContracted && (
                                  <span
                                    className="flex-shrink-0"
                                    data-tooltip-id="recommended-panel-tooltip"
                                    data-tooltip-content={t('Contracted Item')}
                                  >
                                    <ContractedIcon />
                                  </span>
                                )}
                              </div>
                              {/* TODO: Remove the comment when the best match is implemented */}
                              {/* {item.rank === 1 && (
                                <span className="flex-shrink-0 bg-[#DCFCE7] text-[#016630] text-xs font-medium px-2.5 py-1 rounded-full">
                                  {t('Best Match')}
                                </span>
                              )} */}
                            </div>
                            {item.isAvailableInSystem && (
                              <div className="flex flex-wrap items-center gap-x-2">
                                {item?.price && (
                                  <span className="text-[13px]">
                                    <span className="font-semibold text-gray-900">
                                      {item?.price ? `$${Number(item.price).toFixed(2)}` : ''}
                                    </span>
                                    <span className="text-gray-900">
                                      {item.uom ? ` / ${item.uom}` : ''}
                                      {isUomMismatch && <span className="text-red-500">*</span>}
                                    </span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          <div
                            className={`flex min-w-0 max-w-full ${
                              stackDetailActionRow
                                ? 'flex-col items-stretch gap-2'
                                : 'flex-row items-center justify-between gap-4'
                            }`}
                          >
                            <div
                              className={`min-w-0 w-full max-w-full ${
                                stackDetailActionRow ? '' : 'flex-1 basis-0'
                              }`}
                            >
                              <div className="flex min-w-0 text-[13px] gap-1">
                                <span className="flex-shrink-0 text-[#4A5565]">{t('Item#')}:</span>
                                <span className="min-w-0 flex-1 truncate text-[#111111]">
                                  {item.itemNumber}
                                </span>
                              </div>
                              <div className="flex min-w-0 text-[13px] gap-1">
                                <span className="flex-shrink-0 text-[#4A5565]">
                                  {t('Distributor')}:
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[#111111]">
                                  {item.supplierName}
                                </span>
                              </div>
                              <div className="flex min-w-0 text-[13px] gap-1">
                                <span className="flex-shrink-0 text-[#4A5565]">
                                  {t('Manufacturer')}:
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[#111111]">
                                  {item.manufacturerName}
                                </span>
                              </div>
                              <div className="flex min-w-0 text-[13px] gap-1">
                                <span className="flex-shrink-0 text-[#4A5565]">Mfg#:</span>
                                <span className="min-w-0 flex-1 truncate text-[#111111]">
                                  {item.manufacturerNumber}
                                </span>
                              </div>
                            </div>
                            {item.hasAccess &&
                              (getStagedActionType() === 'punchout' ||
                                item.isAvailableInSystem) && (
                                <div
                                  className={`flex max-w-full shrink-0 flex-wrap gap-2 ${
                                    stackDetailActionRow
                                      ? 'w-full justify-start'
                                      : 'w-auto justify-end'
                                  }`}
                                >
                                  {isThisStaged ? (
                                    <div className="max-w-full">
                                      <Button
                                        color="info"
                                        className="inline-flex max-w-full items-center gap-2 whitespace-normal"
                                      >
                                        <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" />
                                        {getActionLabel()}
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      color="ghost"
                                      className="max-w-full whitespace-normal text-left"
                                      onClick={() =>
                                        stageAction(_index, getStagedActionType(), item)
                                      }
                                    >
                                      {getActionLabel()}
                                    </Button>
                                  )}
                                </div>
                              )}
                          </div>

                          {getStagedActionType() === 'punchout' && item.hasAccess ? (
                            <>
                              {page === PAGE_TYPE.PO && Boolean(blockContext?.canDelete) ? (
                                <p className="text-[13px] text-[#D08700]">
                                  {t('punchoutDeletionWarning')}
                                </p>
                              ) : (
                                <p className="text-[13px] text-[#D08700]">
                                  {t('punchoutRedirectWarningMessage')}
                                </p>
                              )}
                            </>
                          ) : item.isAvailableInSystem ? (
                            item.hasAccess ? (
                              <>
                                {getStagedActionType() === 'swap' && (
                                  <p className="text-[13px] text-[#D08700]">
                                    {t('swapItemActionWarningMessage')}
                                  </p>
                                )}
                                {getStagedActionType() === 'addToCart' && (
                                  <p className="text-[13px] text-[#D08700]">
                                    {(page === PAGE_TYPE.PO && blockContext?.canDelete) ||
                                    page === PAGE_TYPE.CART
                                      ? t('addToCartDeleteItemWarningMessage')
                                      : t('addToCartWarningMessage')}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-[13px] text-red-500">{t('hasNoCatalogAccess')}</p>
                            )
                          ) : (
                            <p className="text-[13px] text-red-500">
                              {t('recommendationItemNotAvailable')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div
                      className={`rounded-md border border-gray-200 bg-gray-50 p-2 ${
                        blockContext?.lineItemId ? 'ml-6' : ''
                      }`}
                    >
                      <p className="font-medium text-[13px] text-red-600">
                        {t('noRecommendedAlternativesFound')}
                      </p>
                      {recommendedItem.prefManufacturer && (
                        <>
                          <p className="text-[13px] text-[#000000]">
                            {t('pleaseReviewOptionsWithYourAvendraDistributor')}
                          </p>
                          <div className="mt-2 space-y-0.5">
                            <p className="text-[13px] text-[#000000]">
                              <span className="text-[#4A5565]">
                                {t('Preferred Manufacturer')}:{' '}
                              </span>
                              <span className="font-semibold">
                                {recommendedItem.prefManufacturer.manufacturerName}
                              </span>
                            </p>
                            {recommendedItem.prefManufacturer.manufacturerNo && (
                              <p className="text-[13px] text-[#000000]">
                                <span className="text-[#4A5565]">{t('Mfg#')}: </span>
                                <span>{recommendedItem.prefManufacturer.manufacturerNo}</span>
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );

  if (isInternal) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
        <div className="shrink-0">{headerBar}</div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {showSavingUI && (
            <div className="px-[32px] py-6">
              <SaveRecommendationItems
                stagedActions={stagedActions}
                saveProgress={saveProgress}
                blockDisplayList={blockDisplayList}
                blockContextList={blockContextList}
                pageType={page}
              />
            </div>
          )}
          {!showSavingUI && <div className="px-[32px] py-6">{bodyContent}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
      <div className="sticky top-0 z-10 shrink-0">{headerBar}</div>
      {showSavingUI && (
        <div className="px-[32px] py-6">
          <SaveRecommendationItems
            stagedActions={stagedActions}
            saveProgress={saveProgress}
            blockDisplayList={blockDisplayList}
            blockContextList={blockContextList}
            pageType={page}
          />
        </div>
      )}
      {!showSavingUI && <div className="px-[32px] py-6">{bodyContent}</div>}
    </div>
  );
};

export default RecommendationItems;
